import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import { Link2, Copy, RefreshCw, Trash2, Loader2, ShieldCheck } from 'lucide-react';

// Admin panel: generate / copy / revoke a secure, expiring self-serve onboarding link so a provider can
// fill and sign their own W-9 and NDA without a TFS login. Submissions still require Global Admin verification.
export default function ProviderSelfServeLink({ provider, token, onChanged, idPrefix = '' }) {
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const [busy, setBusy] = useState('');
  const tok = provider?.onboarding_token || '';
  const expiresAt = provider?.onboarding_token_expires_at || null;
  const expired = expiresAt ? new Date(expiresAt) < new Date() : false;
  const url = tok ? `${window.location.origin}/provider-onboarding/${tok}` : '';

  async function generate() {
    setBusy('generate');
    try {
      const r = await axios.post(`${API}/service-providers/${provider.id}/onboarding-link`, {}, h);
      toast.success('Secure onboarding link ready — copy and share it with the provider');
      onChanged?.(r.data);
    } catch { toast.error('Could not generate the link'); }
    finally { setBusy(''); }
  }
  async function revoke() {
    setBusy('revoke');
    try {
      const r = await axios.delete(`${API}/service-providers/${provider.id}/onboarding-link`, h);
      toast.success('Link revoked — the URL no longer works');
      onChanged?.(r.data);
    } catch { toast.error('Could not revoke the link'); }
    finally { setBusy(''); }
  }
  async function copy() {
    try { await navigator.clipboard.writeText(url); toast.success('Link copied to clipboard'); }
    catch { toast.error('Copy failed — select the link and copy manually'); }
  }

  const fmt = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); } catch { return iso; } };

  return (
    <div className="rounded-md border border-[#8FC93A]/50 bg-[#8FC93A]/5 p-2.5" data-testid={`${idPrefix}provider-selfserve-link`}>
      <div className="flex items-center gap-2">
        <Link2 size={14} className="text-[#0E2A47]" />
        <span className="text-sm font-semibold text-[#0E2A47]">Provider self-serve link</span>
        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">No login needed</span>
      </div>
      <p className="mt-1 text-[11px] text-slate-500">
        Give the provider a secure link to fill &amp; sign their W-9 and NDA themselves. Everything they submit
        arrives as <span className="font-semibold">On File · Awaiting Verification</span> — a Global Administrator still verifies it.
      </p>

      {!tok ? (
        <button type="button" onClick={generate} disabled={busy === 'generate'} data-testid={`${idPrefix}selfserve-generate`}
          className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-[#0E2A47] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#16324F] disabled:opacity-50">
          {busy === 'generate' ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />} Generate secure link
        </button>
      ) : (
        <div className="mt-2 space-y-1.5">
          <div className="flex flex-wrap items-center gap-1.5">
            <input readOnly value={url} onFocus={(e) => e.target.select()} data-testid={`${idPrefix}selfserve-url`}
              className="min-w-0 flex-1 rounded border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-700" />
            <button type="button" onClick={copy} data-testid={`${idPrefix}selfserve-copy`}
              className="inline-flex items-center gap-1 rounded-md bg-[#0E2A47] px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-[#16324F]"><Copy size={13} /> Copy</button>
            <button type="button" onClick={generate} disabled={busy === 'generate'} data-testid={`${idPrefix}selfserve-regenerate`}
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100">
              {busy === 'generate' ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Regenerate</button>
            <button type="button" onClick={revoke} disabled={busy === 'revoke'} data-testid={`${idPrefix}selfserve-revoke`}
              className="inline-flex items-center gap-1 rounded-md border border-rose-200 px-2.5 py-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-50">
              {busy === 'revoke' ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />} Revoke</button>
          </div>
          <p className={`text-[11px] ${expired ? 'font-semibold text-rose-600' : 'text-slate-500'}`} data-testid={`${idPrefix}selfserve-expiry`}>
            {expired ? 'This link has expired — regenerate to issue a new one.' : (expiresAt ? `Active · expires ${fmt(expiresAt)}` : 'Active')}
            {provider?.onboarding_last_used_at ? ` · last used ${fmt(provider.onboarding_last_used_at)}` : ''}
          </p>
        </div>
      )}
    </div>
  );
}
